import { IconRalphWiggum } from "@/assets/icons/IconRalphWiggum";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { toast } from "@/components/podkit/toasts/use-toast";
import {
    getExperiment,
    getPendingExperiment,
    setPendingExperiment,
    clearPendingExperiment,
    useExperimentToggle,
    isKnownExperiment,
    ExperimentNames,
} from "@/hooks/use-experiment";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useEffect, useState, type FC } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

/**
 * Captures experiment name from URL and stores it in localStorage.
 *
 * WHY MODULE-LEVEL EXECUTION:
 * This must run at module load time (before React renders) because:
 * 1. User visits /experiments/ralph
 * 2. AuthenticatedUserLayout checks auth → redirects to /login
 * 3. After login → may redirect to /onboarding
 * 4. After onboarding → redirects to / (losing the original URL)
 *
 * By capturing at module load, we store the intent before any redirects.
 * The PendingExperimentActivation component then processes it after auth.
 */
function captureExperimentFromUrl() {
    if (typeof window === "undefined") return;

    const regex = /^\/experiments\/([^/]+)/;
    const match = regex.exec(window.location.pathname);
    if (match?.[1]) {
        const experimentName = match[1];
        const experiment = getExperiment(experimentName);
        if (experiment) {
            setPendingExperiment(experimentName);
        }
    }
}

captureExperimentFromUrl();

function showExperimentEnabledToast(experimentName: string, experimentDisplayName: string) {
    if (experimentName === "ralph") {
        toast({
            title: (
                <span className="flex items-center gap-2">
                    <IconRalphWiggum size={24} />
                    <span>{experimentDisplayName} enabled!</span>
                </span>
            ),
            description: "You can manage experiments in Settings → Experiments",
        });
    } else {
        toast({
            title: `${experimentDisplayName} enabled!`,
            description: "You can manage experiments in Settings → Experiments",
        });
    }
}

/**
 * Page rendered at /experiments/:name.
 * Enables the specific experiment's toggle and redirects to /.
 */
export const ExperimentsActivationPage: FC = () => {
    const { name } = useParams<{ name: string }>();
    const navigate = useNavigate();
    const experimentName = name && isKnownExperiment(name) ? name : undefined;

    // Always call the hook with a valid experiment name (rules of hooks).
    // Use "ralph" as fallback — the hook result is ignored when experimentName is undefined.
    const { enabled, setEnabled, loading } = useExperimentToggle(experimentName ?? ExperimentNames.ralph);
    const [hasActivated, setHasActivated] = useState(false);
    const segmentTrack = useSegmentTrack();

    const experiment = name ? getExperiment(name) : null;

    useEffect(() => {
        if (loading) return;

        if (!experiment && name) {
            toast({
                title: "Experiment not found",
                description: `The experiment "${name}" doesn't exist or is no longer available.`,
            });
            navigate("/", { replace: true });
            return;
        }

        if (experiment && experimentName && !hasActivated) {
            if (!enabled) {
                setEnabled(true);
            }
            setHasActivated(true);

            segmentTrack("experiment_enabled", {
                experiment_name: experimentName,
                experiment_display_name: experiment.name,
                source: "activation_link",
            });

            showExperimentEnabledToast(experimentName, experiment.name);
            navigate("/", { replace: true });
        }
    }, [experiment, experimentName, enabled, setEnabled, hasActivated, loading, name, navigate, segmentTrack]);

    if (hasActivated) {
        return <Navigate to="/" replace />;
    }

    return <LoadingState />;
};

/**
 * Processes any pending experiment activation after login/onboarding.
 * Render this in the main app after authentication is confirmed.
 */
export const PendingExperimentActivation: FC = () => {
    const segmentTrack = useSegmentTrack();

    const pending = getPendingExperiment();
    const pendingName = pending && isKnownExperiment(pending) ? pending : undefined;

    // Always call the hook (rules of hooks). Use "ralph" as fallback.
    const { enabled, setEnabled } = useExperimentToggle(pendingName ?? ExperimentNames.ralph);

    useEffect(() => {
        if (!pending || !pendingName) return;

        const experiment = getExperiment(pending);
        if (!experiment) {
            clearPendingExperiment();
            return;
        }

        clearPendingExperiment();

        if (!enabled) {
            setEnabled(true);
        }

        segmentTrack("experiment_enabled", {
            experiment_name: pendingName,
            experiment_display_name: experiment.name,
            source: "activation_link_after_auth",
        });

        showExperimentEnabledToast(pendingName, experiment.name);
    }, [enabled, setEnabled, segmentTrack, pending, pendingName]);

    return null;
};
